import {
  INDEXER_TITLE_TYPES,
  compileMatrix,
  createEmptyAnswers,
  findIndexingFields,
  isNodeShown,
  type CompiledFieldNode,
  type CompiledForm,
  type CompiledNode,
  type CompiledStaticNode,
  type CompiledTableNode,
  type CompileOptions,
  type CompileTitles,
  type ResponseMarks,
} from '@record-fill';
import type { GeneratedRecordHistory, GeneratedRecordHistoryErpEntry } from '@/entities/GeneratedRecord';
import { formatInstantToPtBR } from '@/shared/lib';
import { buildFillLogsText, isDeviceTitle } from './fill-logs-text';
import type { FillLogsResponseView } from './fill-logs-timeline';

// Response numbers start at 1, so the entry before response 1 can never collide with one.
export const ERP_ENTRY_NUMBER = 0;

type HistoryForErp = Pick<GeneratedRecordHistory, 'structure' | 'rules' | 'permissions' | 'subject'> & {
  matrix: Pick<GeneratedRecordHistory['matrix'], 'id'>;
  record: Pick<GeneratedRecordHistory['record'], 'inspectionType'>;
};

// The flagged fields of an IF or OS matrix, which the ERP filled in response 1; a legacy matrix has
// no flag and keeps its device data in the title elements.
function erpFieldTexts(history: HistoryForErp, titles: CompileTitles): Map<string, string> {
  const texts = new Map<string, string>();
  if (history.record.inspectionType === null) return texts;
  const found = findIndexingFields(history.structure);
  if (!found.ok) return texts;
  const { indexerId, titleIds } = found.fields;
  if (indexerId !== null) texts.set(indexerId, titles.indexer ?? '');
  for (const titleType of INDEXER_TITLE_TYPES) {
    const id = titleIds[titleType];
    if (id !== undefined) texts.set(id, titles.fillTitles?.[titleType] ?? '');
  }
  return texts;
}

export function erpFieldIds(history: HistoryForErp): string[] {
  return [...erpFieldTexts(history, {}).keys()];
}

export function compileErpPhoto(
  history: HistoryForErp,
  entry: GeneratedRecordHistoryErpEntry,
  options: CompileOptions,
): CompiledForm {
  const empty = createEmptyAnswers(history.matrix.id);
  const values = Object.fromEntries(
    [...erpFieldTexts(history, entry.titles)].filter(([, text]) => text.trim().length > 0),
  );
  return compileMatrix({
    structure: history.structure,
    rules: history.rules,
    permissions: history.permissions,
    subject: history.subject,
    answers: { ...empty, values },
    options,
    titles: entry.titles,
  });
}

type ErpNode = CompiledStaticNode | CompiledFieldNode;

// The text of the Mudanças tab never lists a title in the model row of a multiple table, so
// marking one here would paint a title that no change names and the count leaves out. A field of
// that row is listed, in the table block.
function drawnCellNodes(table: CompiledTableNode): CompiledNode[] {
  const shown = (cells: CompiledTableNode['cells'][number]) =>
    cells.flatMap((cell) => (!cell.hidden && cell.node ? [cell.node] : []));
  if (!table.isMultiple) return table.cells.flatMap(shown);
  const modelRow = shown(table.cells.at(-1) ?? []).filter((node) => node.kind === 'field');
  return [...table.cells.slice(0, -1).flatMap(shown), ...modelRow];
}

function drawnErpNodes(nodes: readonly CompiledNode[], fieldIds: ReadonlySet<string>): ErpNode[] {
  return nodes.flatMap((node) => {
    if (!isNodeShown(node)) return [];
    if (isDeviceTitle(node)) return [node];
    if (node.kind === 'field') return fieldIds.has(node.id) ? [node] : [];
    if (node.kind !== 'table') return [];
    return drawnErpNodes(drawnCellNodes(node), fieldIds);
  });
}

function erpNodeText(node: ErpNode): string {
  if (node.kind === 'static') return node.text;
  return typeof node.value === 'string' ? node.value : '';
}

// A title the ERP left blank was not filled, the same way an empty field is never marked filled.
export function erpTitleMarks(form: CompiledForm, fieldIds: readonly string[] = []): ResponseMarks {
  const marks: ResponseMarks = { values: {}, cells: {}, newRows: {} };
  for (const node of drawnErpNodes(form.nodes, new Set(fieldIds))) {
    if (erpNodeText(node).trim().length > 0) marks.values[node.id] = { kind: 'filled' };
  }
  return marks;
}

export function buildErpView(
  form: CompiledForm,
  entry: GeneratedRecordHistoryErpEntry,
  fieldIds: readonly string[] = [],
): FillLogsResponseView {
  const marks = erpTitleMarks(form, fieldIds);
  const { changes } = buildFillLogsText(form, marks, () => '', { deviceTitles: true });
  return {
    kind: 'erp',
    number: ERP_ENTRY_NUMBER,
    authorName: entry.authorLabel,
    instant: formatInstantToPtBR(entry.at, { withTime: true }),
    stageName: '',
    filledCount: changes.length,
    correctedCount: 0,
    correction: false,
    marks,
  };
}

export interface FillLogsErpEntry {
  compiled: CompiledForm;
  view: FillLogsResponseView;
}

// The timeline, the card count and the selection all read this one result, so a sheet that draws no
// device title has no ERP entry anywhere on the screen.
export function buildErpEntry(
  history: HistoryForErp & Pick<GeneratedRecordHistory, 'erpEntry'>,
  options: CompileOptions,
): FillLogsErpEntry | null {
  const entry = history.erpEntry;
  if (!entry) return null;
  const compiled = compileErpPhoto(history, entry, options);
  const fieldIds = erpFieldIds(history);
  if (drawnErpNodes(compiled.nodes, new Set(fieldIds)).length === 0) return null;
  return { compiled, view: buildErpView(compiled, entry, fieldIds) };
}
